import { Icon } from "@iconify/react/dist/iconify.js";
import { Button, message } from "antd";
import clsx from "clsx";
import { Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom/dist";
import usePostData from "../../../api/usePostData";
import AppInput from "../../../common/AppInput";
import AuthFooter from "../../../components/AuthFooter/AuthFooter";
import Heading from "../../../components/Heading/Heading";
import Recaptcha from "../../../components/Recaptcha/Recaptcha";
import { saveUserData } from "../../../redux/slices/authSlice";
import { forgetPasswordResetPasswordValidation } from "../../../utils/validations";
import { RestPasswordTracking } from "../../../utils/mixpanel";

/**
 * A component that represents the reset password step of the forget password process
 *
 * @returns {JSX.Element}
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { email, otp } = location.state || ""; // Fallback to empty string if not found
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const { t } = useTranslation("global");
  const { postData: resetPassword, errors: resetPasswordErrors } =
    usePostData();
  const { postData: detectEmail } = usePostData();
  const { postData: login } = usePostData();

  const initialValues = {
    newPassword: "",
    confirmNewPassword: "",
  };

  const onSubmit = async (values) => {
    if (recaptchaToken == null) {
      return message.error(t("recaptchaRequired"));
    }
    const data = {
      otp: otp,
      new_password: values.newPassword,
    };
    const response = await resetPassword({
      route: "api/method/airport_transport.api.user.update_password",
      data,
      params: {
        headers: {
          recaptchaToken,
        },
      },
    });
    if (response.status !== 200) {
      recaptchaRef?.current.reset();
      setRecaptchaToken(null);
      return;
    }
    const checkUserType = await detectEmail({
      route: `api/method/airport_transport.api.user.detect_email?email=${email}`,
    });
    if (checkUserType.status === 200) {
      const route =
        checkUserType.data.msg === "Transport User"
          ? "api/method/airport_transport.api.user.login"
          : "api/method/airport_transport.api.agent.login";
      const res = await login({
        route,
        data: { usr: email, pwd: values.newPassword },
        params: {
          headers: {
            recaptchaToken,
          },
        },
      });
      const { token, ...userData } = res.data.data;
      dispatch(saveUserData({ token, userData }));
      navigate("/");
      RestPasswordTracking(true);
    }
    recaptchaRef?.current.reset();
  };

  useEffect(() => {
    if (resetPasswordErrors) {
      const errorMsg = resetPasswordErrors?.error || "";
      message.error(`${errorMsg}`);
    }
  }, [resetPasswordErrors]);

  return (
    <div className="sm:w-screen w-full position relative">
      <div className="position absolute left-0 top-0">
        <img
          src="./assets/signin/left_vector.png"
          alt="left_vector"
          className="w-20 h-20 md:w-48 md:h-48"
        />
      </div>
      <div className="position absolute right-0 bottom-0">
        <img
          src="./assets/signin/right_vector.png"
          alt="right_vector"
          className="w-16 md:w-48 h-12 md:h-36"
        />
      </div>

      <div className="z-20 sm:w-screen w-full min-h-screen flex flex-row justify-center items-center">
        <div className="flex flex-col justify-center items-center">
          <button className="mb-4 cursor-pointer" onClick={() => navigate("/")}>
            <img
              src="./assets/header/LogoSVG.svg"
              alt="Moshrouk Trips"
              className="w-16 h-13"
            />
          </button>
          <div className="block w-72 md:w-auto p-5 bg-white border border-gray-200 rounded-lg shadow hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">
            <div>
              <div className="w-auto md:w-[400px] text-left rtl:text-right">
                <Heading
                  title={t("reset_password_text")}
                  className={
                    "text-2xl text-[#0E0E11] text-center md:text-left md:rtl:text-right"
                  }
                />
              </div>
              <div className="text-sm text-text_lightdark_grey text-center md:text-left md:rtl:text-right">
                {t("enter_new_password_text")}
              </div>
            </div>

            <div>
              <Formik
                initialValues={initialValues}
                validationSchema={forgetPasswordResetPasswordValidation(t)}
                onSubmit={onSubmit}
                validate={(values) => {
                  // Return all errors at once
                  return forgetPasswordResetPasswordValidation(t)
                    .validate(values, { abortEarly: false })
                    .then(() => {})
                    .catch((err) => {
                      return err.inner.reduce((obj, e) => {
                        if (!(e.path in obj)) obj[e.path] = [];
                        obj[e.path] = obj[e.path].concat(e.errors);
                        return obj;
                      }, {});
                    });
                }}
              >
                {({ errors, touched, values }) => (
                  <Form>
                    <div>
                      <AppInput
                        label={t("new_password_text")}
                        name="newPassword"
                        type="password"
                        placeholder={t("new_password_text")}
                        hideError
                      />
                    </div>
                    <div className="my-2">
                      <AppInput
                        label={t("confirm_new_password")}
                        name="confirmNewPassword"
                        type="password"
                        placeholder={t("confirm_new_password")}
                        error={errors.confirmNewPassword}
                        touched={touched.confirmNewPassword}
                      />
                    </div>
                    <div className="my-3">
                      <div
                        className={clsx(
                          "my-2 flex flex-row justify-start items-center",
                          {
                            "text-text_steel_blue": values.newPassword,
                            "text-text_warning":
                              errors["newPassword"]?.includes(
                                t("errors.password_min_error")
                              ) ||
                              errors["newPassword"]?.includes(
                                t("errors.password_max_error")
                              ),
                          }
                        )}
                      >
                        <Icon
                          icon="teenyicons:tick-outline"
                          width="24px"
                          height="24px"
                        />
                        <p className="text-sm ms-2">
                          {t("my_profile.password_8_20_text")}
                        </p>
                      </div>

                      <div
                        className={clsx(
                          "my-2 flex flex-row justify-start items-center",
                          {
                            "text-text_steel_blue": values.newPassword,
                            "text-text_warning": errors[
                              "newPassword"
                            ]?.includes(t("errors.password_uppercase_error")),
                          }
                        )}
                      >
                        <Icon
                          icon="teenyicons:tick-outline"
                          width="24px"
                          height="24px"
                        />
                        <p className="text-sm ms-2">
                          {t("my_profile.password_uppercase_text")}
                        </p>
                      </div>

                      <div
                        className={clsx(
                          "my-2 flex flex-row justify-start items-center",
                          {
                            "text-text_steel_blue": values.newPassword,
                            "text-text_warning": errors[
                              "newPassword"
                            ]?.includes(t("errors.password_lowercase_error")),
                          }
                        )}
                      >
                        <Icon
                          icon="teenyicons:tick-outline"
                          width="24px"
                          height="24px"
                        />
                        <p className="text-sm ms-2">
                          {t("my_profile.password_lowercase_text")}
                        </p>
                      </div>

                      <div
                        className={clsx(
                          "my-2 flex flex-row justify-start items-center",
                          {
                            "text-text_steel_blue": values.newPassword,
                            "text-text_warning": errors[
                              "newPassword"
                            ]?.includes(t("errors.password_symbol_error")),
                          }
                        )}
                      >
                        <Icon
                          icon="teenyicons:tick-outline"
                          width="24px"
                          height="24px"
                        />
                        <p className="text-sm ms-2">
                          {t("my_profile.password_symbol_text")}
                        </p>
                      </div>
                    </div>

                    <div>
                      <Recaptcha
                        recaptchaRef={recaptchaRef}
                        onChange={setRecaptchaToken}
                      />
                    </div>

                    <div className="text-center mt-3 md:mt-6">
                      <Button
                        className="bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium rounded text-sm px-5 py-2.5 mb-2"
                        type="submit"
                        htmlType="submit"
                      >
                        {t("submit_text")}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>

          <div className="my-2">
            <AuthFooter />
          </div>
        </div>
      </div>
    </div>
  );
}
